import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CollectionHashTagApiResponse,
  CollectionAiThumbnailApiResponse,
  CollectionKeywordApiResponse,
} from '../apis';

import {
  ApiResponse,
  CollectionFormSnakeCase as CollectionFormRequestParams,
} from '@/shared/types';
import { clientAxios } from '@/shared/api';
import { PersonalDataCountsProps } from '@/features/NewLanding/PersonalDataHeader/PersonalDataCounts';

interface PatchCollectionThumbnailMutationParams {
  id: number;
  imageFile: File | null;
}

export const useCollectionLikeMutation = () => {
  return useMutation({
    mutationFn: ({ id }: { id: number }) => {
      return clientAxios.post('/collectionLikeApi/', { id });
    },
  });
};

export const useCollectionScrapMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number }) => {
      return clientAxios.post('/collectionScrapApi/', { id });
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: ['getProfileIdApi'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getProfileActionLogApi', true],
      });
    },
  });
};

export const useCollectionDeleteMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number }) => {
      return clientAxios.delete('/collectionDeleteApi/', { data: { id } });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['getMyCollectionsApi'] }),
        queryClient.invalidateQueries({ queryKey: ['collectionDetailApi'] }),
        queryClient.invalidateQueries({ queryKey: ['collectionMyDetailApi'] }),
        queryClient.invalidateQueries({
          queryKey: ['collectionCurrentUseApi'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['collectionNewScrapContentsApi'],
        }),
      ]);
    },
  });
};

export const useCollectionEditMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      collection,
    }: {
      id: number;
      collection: CollectionFormRequestParams;
    }) => {
      return clientAxios.put('/collectionEditApi/', {
        id,
        collection,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getMyCollectionsApi'] });
      queryClient.invalidateQueries({ queryKey: ['collectionMyDetailApi'] });
      queryClient.invalidateQueries({ queryKey: ['collectionCurrentUseApi'] });
    },
  });
};

export const useCollectionCreateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      collection,
    }: {
      collection: CollectionFormRequestParams;
    }) => {
      return clientAxios.post('/collectionCreateApi/', { collection });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['getMyCollectionsApi'] }),
        queryClient.invalidateQueries({
          queryKey: ['collectionCurrentUseApi'],
        }),
      ]);
    },
  });
};

export const useCollectionKeywordQuery = ({ enabled }: { enabled: boolean }) =>
  useQuery({
    enabled,
    queryKey: ['collectionKeywordApi'],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionKeywordApiResponse>
      >('/collectionKeywordApi/');

      const uniqueKeywords = Array.from(new Set(data.data.keyword));

      return uniqueKeywords.map((item, index) => ({
        iconUrl: '',
        value: item,
      }));
    },
  });

export const useCollectionHashTagQuery = ({
  q,
  enabled,
  selected,
}: {
  q: string;
  enabled: boolean;
  selected: string;
}) =>
  useQuery({
    enabled,
    queryKey: ['collectionHashTagApi', { current: selected, search: q }],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<CollectionHashTagApiResponse[]>
      >('/collectionHashTagApi/', {
        params: { q, selected },
      });

      return data.data.map((item) => ({
        value: item.name,
        count: item.collection_uses,
      }));
    },
  });

export const usePatchCollectionThumbnailMutation = () => {
  return useMutation({
    mutationFn: async ({
      id,
      imageFile,
    }: PatchCollectionThumbnailMutationParams) => {
      const formData = new FormData();
      formData.append('id', id.toString());
      imageFile && formData.append('image_file', imageFile);

      return clientAxios.patch(`/patchCollectionThumbnailApi/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  });
};

export const useCollectionAiThumbnailMutation = () => {
  return useMutation({
    mutationFn: ({ prompt }: { prompt: string }) => {
      return clientAxios.post<CollectionAiThumbnailApiResponse>(
        '/flux/image/',
        {
          prompt,
        }
      );
    },
  });
};

export const useAddYozmITContentsIntoCollectionMutation = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({
      articleId,
      collectionId,
      externalContentUrl,
    }: {
      articleId?: number;
      collectionId?: number;
      externalContentUrl?: string;
    }) => {
      return clientAxios.post('/addYozmITContentsIntoCollectionApi/', {
        articleId,
        collectionId,
        externalContentUrl,
      });
    },
    onMutate: async ({ articleId, collectionId }) => {
      queryClient.setQueryData(['isAddLoading'], true);

      await queryClient.cancelQueries({
        queryKey: ['getMyCollectionIdsIncludingArticleApi', articleId],
      });
      await queryClient.cancelQueries({
        queryKey: ['getMyCollectionsApi'],
      });
      await queryClient.cancelQueries({
        queryKey: ['collectionMyDetailApi'],
      });

      const previousData = queryClient.getQueryData([
        'getMyCollectionIdsIncludingArticleApi',
        articleId,
      ]);

      queryClient.setQueryData(
        ['getMyCollectionIdsIncludingArticleApi', articleId],
        (old: number[]) => {
          return [...old, collectionId];
        }
      );

      const previousProfileActivitySummary = queryClient.getQueryData([
        'getProfileActivitySummaryApi',
      ]);

      queryClient.setQueryData(
        ['getProfileActivitySummaryApi'],
        (prev: PersonalDataCountsProps) => {
          if (!prev) return;

          return {
            ...prev,
            scrap: prev.scrap + 1,
          };
        }
      );

      return { previousData, previousProfileActivitySummary };
    },
    onError: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: ['getMyCollectionIdsIncludingArticleApi', articleId],
      });
    },
    onSuccess: (_, { articleId, collectionId }) => {
      queryClient.setQueryData(
        ['getMyCollectionIdsIncludingArticleApi', articleId],
        (old: number[]) => {
          return [...old, collectionId];
        }
      );
      queryClient.invalidateQueries({
        queryKey: ['getProfileTutorialStatusApi'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getProfileActionLogApi', true],
      });
    },
    onSettled: (_, __, { articleId }) => {
      queryClient.invalidateQueries({ queryKey: ['getMyCollectionsApi'] });
      queryClient.invalidateQueries({ queryKey: ['collectionMyDetailApi'] });
      queryClient.invalidateQueries({ queryKey: ['articleActionStatusApi'] });
      queryClient.invalidateQueries({ queryKey: ['getProfileIdApi'] });
      queryClient.invalidateQueries({
        queryKey: ['getProfileActivitySummaryApi'],
      });
      queryClient.setQueryData(['isAddLoading'], false);
    },
  });

  const isLoading = queryClient.getQueryData(['isAddLoading']);

  return {
    ...mutation,
    isLoading,
  };
};

export const useAddExternalContentsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      articleId,
      collectionId,
      externalContentUrl,
    }: {
      articleId?: number;
      collectionId?: number;
      externalContentUrl?: string;
    }) => {
      return clientAxios.post('/addYozmITContentsIntoCollectionApi/', {
        articleId,
        collectionId,
        externalContentUrl,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['collectionMyDetailApi'] });
    },
  });
};

export const useDeleteArticleFromCollectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      articleId,
      collectionId,
    }: {
      articleId: number;
      collectionId: number;
    }) => {
      return clientAxios.delete('/deleteArticleFromCollectionApi/', {
        data: { collectionId, articleId },
      });
    },
    onMutate: async ({ articleId, collectionId }) => {
      await queryClient.cancelQueries({
        queryKey: ['getMyCollectionIdsIncludingArticleApi', articleId],
      });
      await queryClient.cancelQueries({
        queryKey: ['getMyCollectionsApi'],
      });
      await queryClient.cancelQueries({
        queryKey: ['collectionMyDetailApi'],
      });

      const previousData = queryClient.getQueryData([
        'getMyCollectionIdsIncludingArticleApi',
        articleId,
      ]);

      queryClient.setQueryData(
        ['getMyCollectionIdsIncludingArticleApi', articleId],
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (old: number[]) => {
          return old.filter((id) => id !== collectionId);
        }
      );

      const previousProfileActivitySummary = queryClient.getQueryData([
        'getProfileActivitySummaryApi',
      ]);

      queryClient.setQueryData(
        ['getProfileActivitySummaryApi'],
        (prev: PersonalDataCountsProps) => {
          if (!prev) return;

          return {
            ...prev,
            scrap: prev.scrap - 1,
          };
        }
      );

      return { previousData, previousProfileActivitySummary };
    },
    onError: (_, { articleId }) => {
      queryClient.invalidateQueries({
        queryKey: ['getMyCollectionIdsIncludingArticleApi', articleId],
      });
    },
    onSettled: (_, __, { articleId }) => {
      queryClient.invalidateQueries({ queryKey: ['getMyCollectionsApi'] });
      queryClient.invalidateQueries({ queryKey: ['collectionMyDetailApi'] });
      queryClient.invalidateQueries({ queryKey: ['articleActionStatusApi'] });
      queryClient.invalidateQueries({
        queryKey: ['getProfileActivitySummaryApi'],
      });
    },
  });
};
